/**
 * Achievements panel — toggle with P. Track player progress against a
 * static catalogue of feats. Progress comes from local counters (kills,
 * quests completed, deaths, levels reached).
 */

import { useEffect, useState } from 'react';
import { useNet } from '../state/net-store.js';
import { levelFromXp } from '../data/leveling.js';
import { useModalStackRegister } from '../state/active-modals-store.js';

interface AchDef {
  id: string;
  title: { ru: string };
  desc: { ru: string };
  category: 'combat' | 'world' | 'social' | 'collect' | 'rite';
  icon: string;
  /** Progress goal — exact meaning depends on the category. */
  target: number;
  /** Reward — only crystals for now; future: title, cosmetic, etc. */
  reward: { crystals: number; title?: string };
}

export const ACHIEVEMENTS: AchDef[] = [
  // Combat
  { id: 'first-blood',     title: { ru: 'Первая кровь' },        desc: { ru: 'Убей своего первого врага.' },               category: 'combat', icon: '🩸', target: 1,    reward: { crystals: 5 } },
  { id: 'hunter',          title: { ru: 'Охотник' },             desc: { ru: 'Убей 25 мобов.' },                            category: 'combat', icon: '🗡️', target: 25,   reward: { crystals: 30 } },
  { id: 'slayer',          title: { ru: 'Истребитель' },         desc: { ru: 'Убей 100 мобов.' },                           category: 'combat', icon: '⚔️',  target: 100,  reward: { crystals: 100, title: 'Истребитель' } },
  { id: 'demonic-100',     title: { ru: 'Демоноборец' },         desc: { ru: 'Убей 100 скелетов.' },                        category: 'combat', icon: '💀', target: 100,  reward: { crystals: 80 } },
  { id: 'wolfslayer',      title: { ru: 'Волкобой' },            desc: { ru: 'Убей 50 волков.' },                           category: 'combat', icon: '🐺', target: 50,   reward: { crystals: 40 } },
  { id: 'world-boss-1',    title: { ru: 'Голос Бури' },          desc: { ru: 'Участвуй в убийстве Властителя Шторма.' },        category: 'combat', icon: '🌩', target: 1,    reward: { crystals: 200, title: 'Тише Шторма' } },
  { id: 'pvp-1',           title: { ru: 'Кровник' },             desc: { ru: 'Убей врага из другой фракции.' },             category: 'combat', icon: '🏴', target: 1,    reward: { crystals: 20 } },
  { id: 'pvp-100',         title: { ru: 'Полководец' },          desc: { ru: 'Убей 100 врагов в Войне Фракций.' },                    category: 'combat', icon: '🎖', target: 100,  reward: { crystals: 300, title: 'Полководец' } },

  // Levels / progression
  { id: 'level-5',         title: { ru: 'Подмастерье' },         desc: { ru: 'Достигни 5 уровня.' },                        category: 'rite',   icon: '✨', target: 5,    reward: { crystals: 25 } },
  { id: 'level-15',        title: { ru: 'Странник' },            desc: { ru: 'Достигни 15 уровня.' },                       category: 'rite',   icon: '🌟', target: 15,   reward: { crystals: 75 } },
  { id: 'level-30',        title: { ru: 'Ветеран Бури' },        desc: { ru: 'Достигни 30 уровня.' },                       category: 'rite',   icon: '🏆', target: 30,   reward: { crystals: 200 } },
  { id: 'level-50',        title: { ru: 'Стормвальдец' },        desc: { ru: 'Достигни 50 уровня — потолок.' },             category: 'rite',   icon: '👑', target: 50,   reward: { crystals: 500, title: 'Стормвальдец' } },

  // Quests
  { id: 'quest-1',         title: { ru: 'Первый шаг' },          desc: { ru: 'Заверши свой первый квест.' },                category: 'world',  icon: '📜', target: 1,    reward: { crystals: 15 } },
  { id: 'quest-10',        title: { ru: 'Помощник Стормвейла' }, desc: { ru: 'Заверши 10 квестов.' },                       category: 'world',  icon: '🗺', target: 10,   reward: { crystals: 100 } },
  { id: 'quest-50',        title: { ru: 'Хроникёр' },            desc: { ru: 'Заверши 50 квестов.' },                       category: 'world',  icon: '📚', target: 50,   reward: { crystals: 500, title: 'Хроникёр' } },

  // Exploration
  { id: 'explore-zones',   title: { ru: 'Картограф' },           desc: { ru: 'Посети все 5 зон.' },                         category: 'world',  icon: '🧭', target: 5,    reward: { crystals: 60 } },

  // Collection
  { id: 'rare-item',       title: { ru: 'Редкая находка' },      desc: { ru: 'Получи редкий (синий) предмет.' },            category: 'collect',icon: '🟦', target: 1,    reward: { crystals: 30 } },
  { id: 'epic-item',       title: { ru: 'Эпическая добыча' },    desc: { ru: 'Получи эпический (фиолетовый) предмет.' },    category: 'collect',icon: '🟪', target: 1,    reward: { crystals: 100 } },
  { id: 'legendary-item',  title: { ru: 'Легенда' },             desc: { ru: 'Получи легендарный (жёлтый) предмет.' },      category: 'collect',icon: '🟨', target: 1,    reward: { crystals: 400, title: 'Легендарный' } },

  // Social
  { id: 'first-party',     title: { ru: 'Не один' },             desc: { ru: 'Сходи в данж в группе.' },                    category: 'social', icon: '🤝', target: 1,    reward: { crystals: 25 } },
  { id: 'guild-warlord',   title: { ru: 'Ярл' },                 desc: { ru: 'Возглавь войско в зональной победе.' },       category: 'social', icon: '🏰', target: 1,    reward: { crystals: 250, title: 'Ярл' } },

  // Death
  { id: 'die-hard',        title: { ru: 'Из седла' },            desc: { ru: 'Умри 10 раз — сильным становишься от падений.' }, category: 'rite', icon: '💔', target: 10,  reward: { crystals: 15 } },

  // ── R138: расширенный WotLK 3.3.5a-style achievement пул (40+ доп.) ──

  // Combat расширения по mob kind
  { id: 'kill-50-bandits',  title: { ru: 'Истребитель Разбойников' }, desc: { ru: 'Убей 50 разбойников.' },                     category: 'combat', icon: '🥷', target: 50,  reward: { crystals: 50 } },
  { id: 'kill-50-goblins',  title: { ru: 'Гоблинобой' },          desc: { ru: 'Убей 50 гоблинов.' },                          category: 'combat', icon: '👺', target: 50,  reward: { crystals: 50 } },
  { id: 'kill-25-orcs',     title: { ru: 'Орчий Враг' },          desc: { ru: 'Убей 25 орков.' },                              category: 'combat', icon: '🪓', target: 25,  reward: { crystals: 60 } },
  { id: 'kill-50-spiders',  title: { ru: 'Аранхофоб' },           desc: { ru: 'Убей 50 пауков.' },                             category: 'combat', icon: '🕷️', target: 50,  reward: { crystals: 50 } },
  { id: 'kill-10-dragons',  title: { ru: 'Драконобой' },          desc: { ru: 'Убей 10 драконидов.' },                         category: 'combat', icon: '🐉', target: 10,  reward: { crystals: 250, title: 'Драконобой' } },
  { id: 'kill-10-demons',   title: { ru: 'Демоноборец' },         desc: { ru: 'Убей 10 демонов.' },                            category: 'combat', icon: '😈', target: 10,  reward: { crystals: 200 } },
  { id: 'crit-1000',        title: { ru: 'Острый Глаз' },         desc: { ru: 'Нанеси 1000 критических ударов.' },             category: 'combat', icon: '🎯', target: 1000, reward: { crystals: 150 } },
  { id: 'damage-million',   title: { ru: 'Миллион Урона' },       desc: { ru: 'Нанеси 1 000 000 урона за карьеру.' },          category: 'combat', icon: '💥', target: 1000000, reward: { crystals: 300, title: 'Палач' } },
  { id: 'heal-million',     title: { ru: 'Спаситель' },           desc: { ru: 'Восстанови 1 000 000 HP в группе.' },           category: 'combat', icon: '💚', target: 1000000, reward: { crystals: 300, title: 'Спаситель' } },
  { id: 'no-death-1h',      title: { ru: 'Бессмертный' },         desc: { ru: 'Час игры без единой смерти.' },                category: 'combat', icon: '🛡️', target: 1,    reward: { crystals: 100 } },

  // Levels расширение
  { id: 'level-10',         title: { ru: 'Новичок' },             desc: { ru: 'Достигни 10 уровня.' },                         category: 'rite',   icon: '🌱', target: 10,  reward: { crystals: 50 } },
  { id: 'level-20',         title: { ru: 'Боец' },                desc: { ru: 'Достигни 20 уровня.' },                         category: 'rite',   icon: '⚔️',  target: 20,  reward: { crystals: 100 } },
  { id: 'level-40',         title: { ru: 'Опытный' },             desc: { ru: 'Достигни 40 уровня.' },                         category: 'rite',   icon: '🎓', target: 40,  reward: { crystals: 300 } },
  { id: 'level-60',         title: { ru: 'Чемпион Бури' },        desc: { ru: 'Достигни максимального 60 уровня.' },           category: 'rite',   icon: '👑', target: 60,  reward: { crystals: 1000, title: 'Чемпион Бури' } },

  // Exploration расширение
  { id: 'explore-stormwind',title: { ru: 'Гость Львиной Твердыни' },    desc: { ru: 'Посети Львиная Твердыня.' },                           category: 'world',  icon: '🏰', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-orgrimmar',title: { ru: 'Гость Цитадели Бранного Колеса' },    desc: { ru: 'Посети Цитадель Бранного Колеса.' },                           category: 'world',  icon: '🏯', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-darnassus',title: { ru: 'Под Древом' },          desc: { ru: 'Посети Лунная Ива.' },                              category: 'world',  icon: '🌳', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-ironforge',title: { ru: 'В Кузницах' },          desc: { ru: 'Посети Железный Костёр.' },                           category: 'world',  icon: '⚒️', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-undercity',title: { ru: 'В Тёмном Склепе' },         desc: { ru: 'Посети Тёмный Склеп.' },                            category: 'world',  icon: '☠️', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-thunderbluff', title: { ru: 'На Утёсе' },        desc: { ru: 'Посети Грозовой Утёс.' },                       category: 'world',  icon: '⛰️', target: 1,    reward: { crystals: 30 } },
  { id: 'explore-all-zones',title: { ru: 'Картограф Мира' },      desc: { ru: 'Посети все 41 зону мира.' },                    category: 'world',  icon: '🗺️', target: 41,   reward: { crystals: 800, title: 'Картограф' } },

  // Quests расширение
  { id: 'quest-100',        title: { ru: 'Эпопея' },              desc: { ru: 'Заверши 100 квестов.' },                        category: 'world',  icon: '📜', target: 100,  reward: { crystals: 1000, title: 'Эпик-герой' } },
  { id: 'quest-class',      title: { ru: 'Дисциплина' },          desc: { ru: 'Заверши класс-квест.' },                        category: 'world',  icon: '🎓', target: 1,    reward: { crystals: 100 } },
  { id: 'quest-elite',      title: { ru: 'Элитная Серия' },       desc: { ru: 'Заверши 5 элитных квестов.' },                  category: 'world',  icon: '⭐', target: 5,    reward: { crystals: 250 } },

  // Dungeon achievements
  { id: 'dungeon-1',        title: { ru: 'Первый Подземник' },    desc: { ru: 'Пройди своё первое подземелье.' },             category: 'social', icon: '🕯️', target: 1,    reward: { crystals: 75 } },
  { id: 'dungeon-deadmines',title: { ru: 'Покоритель Шахт' },     desc: { ru: 'Пройди Мёртвые Шахты.' },                      category: 'social', icon: '⛏️', target: 1,    reward: { crystals: 100 } },
  { id: 'dungeon-stockades',title: { ru: 'Тюремщик' },            desc: { ru: 'Пройди Тюрьму Львиной Твердыни.' },                  category: 'social', icon: '🔒', target: 1,    reward: { crystals: 100 } },
  { id: 'dungeon-wailing',  title: { ru: 'Воющие Пещеры' },       desc: { ru: 'Пройди Воющие Пещеры.' },                       category: 'social', icon: '🐍', target: 1,    reward: { crystals: 100 } },
  { id: 'dungeon-scarlet',  title: { ru: 'Алый Монастырь' },      desc: { ru: 'Пройди все 4 крыла Монастыря.' },               category: 'social', icon: '✝️', target: 4,    reward: { crystals: 400, title: 'Палач Багровой' } },
  { id: 'dungeon-strath',   title: { ru: 'Истребитель Чумы' },    desc: { ru: 'Пройди Чумная Стратоморь.' },                          category: 'social', icon: '☠️', target: 1,    reward: { crystals: 300 } },
  { id: 'dungeon-naxx',     title: { ru: 'Защитник Лордерона' },  desc: { ru: 'Пройди Наксрамас.' },                           category: 'social', icon: '🦴', target: 1,    reward: { crystals: 1500, title: 'Защитник Лордерона' } },

  // Raid achievements
  { id: 'raid-mc',          title: { ru: 'Молочные Глубины' },    desc: { ru: 'Пройди Огненные Недра.' },                      category: 'social', icon: '🔥', target: 1,    reward: { crystals: 800, title: 'Огненный' } },
  { id: 'raid-onyxia',      title: { ru: 'Драконоборец' },        desc: { ru: 'Убей Ониксию.' },                               category: 'social', icon: '🐲', target: 1,    reward: { crystals: 1000, title: 'Драконоборец' } },
  { id: 'raid-bwl',         title: { ru: 'Логово Чёрного Крыла' }, desc: { ru: 'Пройди Логово Крыла Тьмы.' },                  category: 'social', icon: '🐉', target: 1,    reward: { crystals: 1500 } },

  // PvP расширение
  { id: 'bg-warsong',       title: { ru: 'Ущелье Песни Войны' },  desc: { ru: 'Победи в Ущелье Песни Войны.' },               category: 'combat', icon: '🚩', target: 1,    reward: { crystals: 100 } },
  { id: 'bg-arathi',        title: { ru: 'Котёл Аратии' },        desc: { ru: 'Победи в Котле Аратии.' },                      category: 'combat', icon: '⚔️', target: 1,    reward: { crystals: 100 } },
  { id: 'bg-alterac',       title: { ru: 'Альтеракская Долина' }, desc: { ru: 'Победи в Альтераке.' },                         category: 'combat', icon: '⛰️', target: 1,    reward: { crystals: 200 } },
  { id: 'rank-grand-marshal',title:{ ru: 'Маршал' },              desc: { ru: 'Достигни ранга 14 Маршал в Войне Фракций.' },             category: 'combat', icon: '🎖', target: 14,   reward: { crystals: 3000, title: 'Великий Маршал' } },

  // Professions
  { id: 'prof-mining-100',  title: { ru: 'Шахтёр' },              desc: { ru: 'Подними Горное дело до 100.' },                category: 'collect',icon: '⛏️', target: 100,  reward: { crystals: 100 } },
  { id: 'prof-herb-100',    title: { ru: 'Травник' },             desc: { ru: 'Подними Травничество до 100.' },               category: 'collect',icon: '🌿', target: 100,  reward: { crystals: 100 } },
  { id: 'prof-skin-100',    title: { ru: 'Кожевник' },            desc: { ru: 'Подними Свежевание до 100.' },                 category: 'collect',icon: '🔪', target: 100,  reward: { crystals: 100 } },
  { id: 'prof-cook-225',    title: { ru: 'Повар-Эксперт' },       desc: { ru: 'Подними Кулинарию до 225.' },                  category: 'collect',icon: '🍖', target: 225,  reward: { crystals: 200 } },
  { id: 'prof-fish-300',    title: { ru: 'Мастер Рыбалки' },      desc: { ru: 'Подними Рыбалку до 300 (max).' },              category: 'collect',icon: '🎣', target: 300,  reward: { crystals: 500, title: 'Морской Волк' } },
  { id: 'prof-master-craft',title: { ru: 'Грандмастер' },         desc: { ru: 'Достигни 300 в любой ремесленной профессии.' },   category: 'collect',icon: '🏆', target: 300,  reward: { crystals: 1000, title: 'Грандмастер' } },

  // Mounts / cosmetic
  { id: 'mount-first',      title: { ru: 'Всадник' },             desc: { ru: 'Получи свой первый верховой маунт.' },          category: 'collect',icon: '🐴', target: 1,    reward: { crystals: 50 } },
  { id: 'mount-3',          title: { ru: 'Конюх' },               desc: { ru: 'Собери 3 разных маунта.' },                     category: 'collect',icon: '🏇', target: 3,    reward: { crystals: 200 } },

  // Gold / economy
  { id: 'gold-100',         title: { ru: 'Сотня' },               desc: { ru: 'Накопи 100 Storm Crystals.' },                  category: 'collect',icon: '💎', target: 100,  reward: { crystals: 50 } },
  { id: 'gold-1000',        title: { ru: 'Тысячник' },            desc: { ru: 'Накопи 1000 Storm Crystals.' },                 category: 'collect',icon: '💰', target: 1000, reward: { crystals: 200 } },

  // Social расширение
  { id: 'guild-join',       title: { ru: 'Братство' },            desc: { ru: 'Вступи в гильдию.' },                           category: 'social', icon: '🛡️', target: 1,    reward: { crystals: 50 } },
  { id: 'duel-10',          title: { ru: 'Дуэлянт' },             desc: { ru: 'Выиграй 10 дуэлей.' },                          category: 'social', icon: '🤺', target: 10,   reward: { crystals: 100 } },

  // Emote / fluff
  { id: 'emote-dance-10',   title: { ru: 'Танцор' },              desc: { ru: 'Станцуй /dance 10 раз.' },                      category: 'social', icon: '💃', target: 10,   reward: { crystals: 30 } },
  { id: 'emote-bow-50',     title: { ru: 'Учтивый' },             desc: { ru: 'Поклонись /bow 50 раз.' },                      category: 'social', icon: '🙇', target: 50,   reward: { crystals: 50 } },

  // ── R181: +30 WotLK 3.3.5a achievements: world bosses, mounts, профессии, holidays, fluff. ──
  // World bosses
  { id: 'wb-kazzak',        title: { ru: 'Сорвал маску демона' }, desc: { ru: 'Участвуй в убийстве Лорда Каззака.' },          category: 'combat', icon: '👹', target: 1,    reward: { crystals: 500, title: 'Победитель Каззака' } },
  { id: 'wb-azuregos',      title: { ru: 'Лазурный Покой' },      desc: { ru: 'Участвуй в убийстве Азурегоса.' },              category: 'combat', icon: '🐉', target: 1,    reward: { crystals: 500, title: 'Лазурный убийца' } },
  { id: 'wb-doomwalker',    title: { ru: 'Земля Дрожит' },        desc: { ru: 'Участвуй в убийстве Громового Странника.' },    category: 'combat', icon: '⚡', target: 1,    reward: { crystals: 800 } },
  { id: 'wb-all-dragons',   title: { ru: 'Драконьи Кошмары' },    desc: { ru: 'Убей всех 4 Изумрудных Драконов (Эмерисс, Тэрар, Изондра, Летон).' }, category: 'combat', icon: '🐲', target: 4, reward: { crystals: 2000, title: 'Палач Кошмаров' } },

  // Mounts расширение
  { id: 'mount-5',          title: { ru: 'Стойло' },              desc: { ru: 'Собери 5 разных маунтов.' },                    category: 'collect', icon: '🐴', target: 5,    reward: { crystals: 400 } },
  { id: 'mount-all-faction',title: { ru: 'Имперская конюшня' },   desc: { ru: 'Собери все 4 фракционных маунта своей стороны.' },category: 'collect', icon: '🐎', target: 4,    reward: { crystals: 800, title: 'Имперский Всадник' } },
  { id: 'mount-epic',       title: { ru: 'Эпический скакун' },    desc: { ru: 'Получи эпический маунт (+60% к скорости).' },   category: 'collect', icon: '🦄', target: 1,    reward: { crystals: 600 } },

  // Profession milestones
  { id: 'prof-smith-225',   title: { ru: 'Мастер кузнечного дела' }, desc: { ru: 'Подними Кузнечное дело до 225.' },          category: 'collect', icon: '⚒', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-tailor-225',  title: { ru: 'Мастер портняжного дела' },desc: { ru: 'Подними Портняжное дело до 225.' },         category: 'collect', icon: '🧵', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-alchemy-225', title: { ru: 'Мастер алхимии' },      desc: { ru: 'Подними Алхимию до 225.' },                     category: 'collect', icon: '⚗', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-leather-225', title: { ru: 'Мастер кожевничества' },desc: { ru: 'Подними Кожевничество до 225.' },               category: 'collect', icon: '🪡', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-enchant-225', title: { ru: 'Мастер чар' },          desc: { ru: 'Подними Наложение чар до 225.' },               category: 'collect', icon: '✨', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-eng-225',     title: { ru: 'Мастер инженерного дела' }, desc: { ru: 'Подними Инженерное дело до 225.' },         category: 'collect', icon: '🔧', target: 225,  reward: { crystals: 250 } },
  { id: 'prof-2-arts',      title: { ru: 'Двуручный мастер' },    desc: { ru: '2 любые ремесленные профессии до 300.' },       category: 'collect', icon: '🏆', target: 2,    reward: { crystals: 1500, title: 'Двуручный Мастер' } },

  // Battlegrounds
  { id: 'bg-flag-cap',      title: { ru: 'Знаменосец' },          desc: { ru: 'Захвати флаг 10 раз в Ущелье Песен Войны.' },   category: 'combat', icon: '🏳️', target: 10,   reward: { crystals: 200 } },
  { id: 'bg-flag-defend',   title: { ru: 'Защитник Флага' },      desc: { ru: 'Помешай захвату флага 25 раз.' },               category: 'combat', icon: '🛡', target: 25,   reward: { crystals: 200 } },
  { id: 'bg-arathi-defend', title: { ru: 'Стратегический разум' },desc: { ru: 'Защити 5 точек в Аратийской Котловине.' },      category: 'combat', icon: '🏛', target: 5,    reward: { crystals: 200 } },
  { id: 'pvp-honor-rank-7', title: { ru: 'Командир' },            desc: { ru: 'Достигни ранга 7 (Командир).' },                category: 'combat', icon: '🎖', target: 7,    reward: { crystals: 800 } },
  { id: 'pvp-1000-kills',   title: { ru: 'Опытный воин' },        desc: { ru: 'Убей 1000 врагов в открытой Войне Фракций.' },  category: 'combat', icon: '⚔', target: 1000, reward: { crystals: 1500, title: 'Опытный воин' } },

  // Reputation
  { id: 'rep-stormwind-exalted', title: { ru: 'Друг Львиной Твердыни' },desc: { ru: 'Достигни Превознесения с Львиной Твердыней.' },       category: 'social', icon: '🦁', target: 1,    reward: { crystals: 500, title: 'Друг Львиной Твердыни' } },
  { id: 'rep-orgrimmar-exalted', title: { ru: 'Друг Цитадели Бранного Колеса' },desc: { ru: 'Достигни Превознесения с Цитаделью Бранного Колеса.' },       category: 'social', icon: '🐺', target: 1,    reward: { crystals: 500, title: 'Друг Цитадели Бранного Колеса' } },
  { id: 'rep-3-exalted',    title: { ru: 'Дипломат' },             desc: { ru: 'Достигни Превознесения с 3 фракциями.' },       category: 'social', icon: '🤝', target: 3,    reward: { crystals: 1000, title: 'Дипломат' } },

  // Social холидэи
  { id: 'fishing-50',       title: { ru: 'Рыбак' },                desc: { ru: 'Поймай 50 рыб.' },                              category: 'collect', icon: '🐟', target: 50,   reward: { crystals: 100 } },
  { id: 'fishing-rare',     title: { ru: 'Редкий улов' },          desc: { ru: 'Поймай рыбу редкого качества.' },               category: 'collect', icon: '🎣', target: 1,    reward: { crystals: 200 } },
  { id: 'cooking-meals',    title: { ru: 'Гурман' },               desc: { ru: 'Приготовь 100 блюд.' },                         category: 'collect', icon: '🍲', target: 100,  reward: { crystals: 150 } },

  // Wealth
  { id: 'gold-10000',       title: { ru: 'Богатей' },              desc: { ru: 'Накопи 10 000 Storm Crystals.' },               category: 'collect', icon: '💎', target: 10000,reward: { crystals: 500, title: 'Богатей' } },
  { id: 'auction-100',      title: { ru: 'Торгаш' },               desc: { ru: 'Продай 100 предметов на Аукционе.' },           category: 'social', icon: '🏷', target: 100,  reward: { crystals: 300 } },

  // Loot rarity
  { id: 'set-tier1',        title: { ru: 'Совершенство 1-го Уровня' }, desc: { ru: 'Соберь полный сет 1-го Уровня (8 предметов).' },  category: 'collect', icon: '🛡', target: 8,    reward: { crystals: 2500, title: 'Чемпион 1-го Уровня' } },

  // Class quests
  { id: 'class-quest-10',   title: { ru: 'Дисциплина' },           desc: { ru: 'Заверши свой классовый квест на 10 уровне.' },  category: 'world',  icon: '🎓', target: 1,    reward: { crystals: 200, title: 'Посвящённый' } },

  // Misc / fluff
  { id: 'emote-cheer-100',  title: { ru: 'Болельщик' },            desc: { ru: '/cheer 100 раз.' },                             category: 'social', icon: '📣', target: 100,  reward: { crystals: 50 } },
  { id: 'macros-10',        title: { ru: 'Программист' },          desc: { ru: 'Создай 10 макросов.' },                         category: 'social', icon: '⌨', target: 10,   reward: { crystals: 80 } },
  { id: 'jump-1000',        title: { ru: 'Кузнечик' },             desc: { ru: 'Прыгни 1000 раз.' },                            category: 'social', icon: '🦘', target: 1000, reward: { crystals: 60 } },
];

const STORAGE_KEY = 'aiwari-achievements';

interface Progress {
  kills: number;
  questsCompleted: number;
  deaths: number;
  highestLevel: number;
  zonesVisited: string[];
  rareItems: number;
  epicItems: number;
  legendaryItems: number;
  pvpKills: number;
  worldBossKills: number;
  unlocked: string[];
}

function load(): Progress {
  try {
    const j = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return {
      kills: j.kills ?? 0,
      questsCompleted: j.questsCompleted ?? 0,
      deaths: j.deaths ?? 0,
      highestLevel: j.highestLevel ?? 1,
      zonesVisited: Array.isArray(j.zonesVisited) ? j.zonesVisited : [],
      rareItems: j.rareItems ?? 0,
      epicItems: j.epicItems ?? 0,
      legendaryItems: j.legendaryItems ?? 0,
      pvpKills: j.pvpKills ?? 0,
      worldBossKills: j.worldBossKills ?? 0,
      unlocked: Array.isArray(j.unlocked) ? j.unlocked : [],
    };
  } catch {
    return { kills: 0, questsCompleted: 0, deaths: 0, highestLevel: 1, zonesVisited: [], rareItems: 0, epicItems: 0, legendaryItems: 0, pvpKills: 0, worldBossKills: 0, unlocked: [] };
  }
}

export function Achievements() {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState<Progress>(() => load());
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);
  const self = selfId ? players[selfId] : null;
  const level = self ? levelFromXp(self.xp ?? 0).level : 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'p' || e.key === 'P' || e.key === 'з' || e.key === 'З') {
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // R232q5az M01: register on global modal stack.
  useModalStackRegister('achievements', open, () => setOpen(false));

  // Cheap re-poll so the UI updates as kills happen.
  useEffect(() => {
    const id = window.setInterval(() => setProgress(load()), 2500);
    return () => window.clearInterval(id);
  }, []);

  // Pull highest level from the live player state.
  useEffect(() => {
    if (level > progress.highestLevel) {
      const next = { ...progress, highestLevel: level };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      setProgress(next);
    }
  }, [level, progress]);

  if (!open) return null;

  const progressFor = (a: AchDef): number => {
    switch (a.id) {
      case 'first-blood':
      case 'hunter':
      case 'slayer':
        return Math.min(a.target, progress.kills);
      case 'wolfslayer':
      case 'demonic-100':
        return Math.min(a.target, progress.kills);  // approximate; per-kind tracking later
      case 'world-boss-1':
        return Math.min(a.target, progress.worldBossKills);
      case 'pvp-1':
      case 'pvp-100':
        return Math.min(a.target, progress.pvpKills);
      case 'level-5':
      case 'level-15':
      case 'level-30':
      case 'level-50':
        return Math.min(a.target, progress.highestLevel);
      case 'quest-1':
      case 'quest-10':
      case 'quest-50':
        return Math.min(a.target, progress.questsCompleted);
      case 'explore-zones':
        return Math.min(a.target, progress.zonesVisited.length);
      case 'rare-item':
        return Math.min(a.target, progress.rareItems);
      case 'epic-item':
        return Math.min(a.target, progress.epicItems);
      case 'legendary-item':
        return Math.min(a.target, progress.legendaryItems);
      case 'die-hard':
        return Math.min(a.target, progress.deaths);
      default:
        return progress.unlocked.includes(a.id) ? a.target : 0;
    }
  };

  const completed = ACHIEVEMENTS.filter((a) => progressFor(a) >= a.target).length;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm pointer-events-auto"
         onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div
        className="relative rounded-2xl p-0 max-w-[860px] w-[94vw] max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #1A1108 0%, #0A0604 100%)',
          border: '2px solid #C9A96A',
          boxShadow: '0 0 60px rgba(251,191,36,0.3)',
          fontFamily: 'system-ui',
        }}
      >
        <div className="flex items-baseline justify-between p-5 border-b" style={{ borderColor: 'rgba(251,191,36,0.3)' }}>
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#E8DCC0]Muted">Подвиги</div>
            <h2 className="text-3xl font-bold text-[#E8C77B]" style={{ fontFamily: 'Cinzel, serif' }}>Достижения</h2>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-[#E8DCC0]Muted tracking-wider">Открыто</div>
            <div className="text-2xl font-bold text-[#E8C77B]">{completed} / {ACHIEVEMENTS.length}</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-[#E8DCC0]Muted hover:text-[#E8DCC0] text-xl px-2 self-center">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ACHIEVEMENTS.map((a) => {
              const cur = progressFor(a);
              const done = cur >= a.target;
              const pct = (cur / a.target) * 100;
              return (
                <div
                  key={a.id}
                  className="rounded-lg p-3 flex items-start gap-3 transition"
                  style={{
                    background: done ? 'linear-gradient(90deg,rgba(251,191,36,0.18),transparent)' : 'rgba(20,16,10,0.6)',
                    border: done ? '1px solid rgba(251,191,36,0.7)' : '1px solid rgba(80,60,30,0.4)',
                    opacity: done ? 1 : 0.85,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-3xl flex-shrink-0"
                    style={{
                      background: done ? 'linear-gradient(135deg,#C9A96A,#C9A96A)' : 'rgba(40,30,20,0.7)',
                      border: '1px solid rgba(251,191,36,0.5)',
                      filter: done ? 'none' : 'grayscale(0.7) brightness(0.6)',
                    }}
                  >
                    {a.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-bold text-[#E8DCC0]" style={{ fontFamily: 'Cinzel, serif' }}>
                        {done && '✓ '}{a.title.ru}
                      </div>
                      <div className="text-[10px] text-[#E8DCC0]Muted font-mono">{cur}/{a.target}</div>
                    </div>
                    <div className="text-[11px] text-[#E8DCC0]Dim mt-0.5 leading-snug">{a.desc.ru}</div>
                    <div className="h-1 bg-[#2A1F18] rounded-full mt-1.5 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: done ? '#C9A96A' : '#84CC16' }} />
                    </div>
                    <div className="text-[10px] text-[#E8DCC0]Muted mt-1 flex items-center gap-2">
                      <span>+{a.reward.crystals} ✦</span>
                      {a.reward.title && <span className="text-[#C9A96A] italic">титул «{a.reward.title}»</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-[10px] text-[#E8DCC0]Muted px-5 py-2 border-t flex items-center justify-between"
             style={{ borderColor: 'rgba(251,191,36,0.3)' }}>
          <span>P — закрыть</span>
          <span style={{ fontFamily: 'Cinzel, serif' }}>Стормвейл · Архив подвигов</span>
        </div>
      </div>
    </div>
  );
}

/** Bumper helper — call from kill / quest complete / level-up sites to
 *  push counters into localStorage. The achievements panel re-polls every
 *  2.5s so progress shows up automatically. */
export function bumpAchievementCounter(field: keyof Omit<Progress, 'zonesVisited' | 'unlocked'>, by = 1): void {
  const cur = load();
  (cur[field] as number) += by;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cur)); } catch {}
}

export function recordZoneVisited(zoneId: string): void {
  if (!zoneId) return;
  const cur = load();
  if (!cur.zonesVisited.includes(zoneId)) {
    cur.zonesVisited.push(zoneId);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cur)); } catch {}
  }
}
